import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { PROFILEMODAL, ACCOUNT, BILLING, INSIGHTS, TEAM_MEMBERS, CALL_SCRIPTS, VERIFICATIONS } from "utils/reducers";
import Searchbar from "pages/MVP/components/Searchbar/Searchbar";
import { isCamberWhitelabel } from "utils/utils";
import { Button } from "components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "components/ui/avatar";
import { Settings, Menu, HelpCircle } from "lucide-react";
import HelpDialog from "pages/MVP/components/Sidebar/HelpDialog";

// Logo assets served from public directory
import Logo from "../../pages/MVP/assets/Logo.png";
import CamberLogo from "../../pages/MVP/assets/camber_logo_transparent.png";

interface User {
  first_name: string;
  picture_url: string;
  team_id: string;
}

interface HeaderProps {
  currentUser: User;
  noSearch?: boolean;
  onMenuClick: () => void;
  searchPatients?: (query: string) => Promise<void>;
  onClearSearch?: () => void;
}

function Header({
  currentUser,
  noSearch,
  onMenuClick,
  searchPatients,
  onClearSearch,
}: HeaderProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showHelpDialog, setShowHelpDialog] = useState(false);

  const toDashboard = () => {
    dispatch({ type: PROFILEMODAL.hide });
    dispatch({ type: ACCOUNT.hide });
    dispatch({ type: BILLING.hide });
    dispatch({ type: INSIGHTS.hide });
    dispatch({ type: TEAM_MEMBERS.hide });
    dispatch({ type: CALL_SCRIPTS.hide });
    dispatch({ type: VERIFICATIONS.hide });
    navigate("/dashboard");
  };

  const logo_path = isCamberWhitelabel() ? CamberLogo : Logo;

  return (
    <header className="h-12 md:h-16 w-full flex items-center px-3 md:px-0 py-2 md:py-0">
      <div className="flex items-center">
        {/* Hamburger Menu - Mobile Only */}
        <button
          onClick={onMenuClick}
          className="md:hidden mr-1 text-gray-500 hover:text-gray-700"
        >
          <Menu className="h-5 w-5 md:h-6 md:w-6" />
        </button>

        {/* Left Section - Logo and Title (Desktop) */}
        <div className="hidden md:flex items-center space-x-4 w-64 px-4 md:px-6 flex-shrink-0">
          <img
            src={logo_path}
            className={`cursor-pointer hover:opacity-70 transition-opacity ${isCamberWhitelabel() ? "w-12 h-12" : "w-12 h-12 rounded-full"
              }`}
            alt={isCamberWhitelabel() ? "Camber" : "Health Harbor"}
            onClick={toDashboard}
          />
          <h1 className="text-xl font-semibold tracking-tight font-inter text-brand">
            {isCamberWhitelabel() ? "Camber Benefits Portal" : "Health Harbor"}
          </h1>
        </div>
      </div>

      {/* Search Section - Aligned with main content */}
      {!noSearch && (
        <div className="flex-1 px-1 md:px-2 pr-2 md:pr-4">
          <div className="max-w-2xl">
            <Searchbar
              searchPatients={searchPatients}
              onClearSearch={onClearSearch}
            />
          </div>
        </div>
      )}

      {/* Right Section - Account Controls */}
      <div className="flex items-center space-x-2 md:px-6 ml-auto">
        {/* Account Button - Desktop Only */}
        <Button
          variant="ghost"
          size="lg"
          className="hidden md:flex text-gray-600 hover:text-gray-900 hover:bg-gray-200 font-normal px-3 py-2 rounded-3xl"
          onClick={() => {
            dispatch({ type: PROFILEMODAL.hide });
            dispatch({ type: BILLING.hide });
            dispatch({ type: INSIGHTS.hide });
            dispatch({ type: TEAM_MEMBERS.hide });
            dispatch({ type: CALL_SCRIPTS.hide });
            dispatch({ type: VERIFICATIONS.hide });
            dispatch({ type: ACCOUNT.display });
            navigate("/dashboard");
          }}
        >
          <Settings className="w-4 h-4 mr-1.5" />
          <span>Account</span>
        </Button>

        {/* Help/Contact Us Button - Desktop Only */}
        <Button
          variant="ghost"
          size="lg"
          className="hidden md:flex text-gray-600 hover:text-gray-900 hover:bg-gray-200 font-normal px-3 py-2 rounded-3xl"
          onClick={() => setShowHelpDialog(true)}
        >
          <HelpCircle className="w-4 h-4 mr-1.5" />
          <span>Help</span>
        </Button>

        {/* User Profile Button */}
        <Button
          variant="ghost"
          size="sm"
          className="flex items-center space-x-1.5 md:space-x-2.5 h-8 md:h-10 px-2 md:px-3 rounded-full hover:bg-gray-200 bg-white"
          onClick={() => dispatch({ type: PROFILEMODAL.toggle })}
        >
          <span className="text-sm font-normal text-gray-700 hidden sm:block">
            {currentUser.first_name}
          </span>
          <Avatar className="w-6 h-6 md:w-7 md:h-7">
            <AvatarImage
              src={currentUser.picture_url}
              alt={`${currentUser.first_name}'s avatar`}
            />
            <AvatarFallback className="text-xs bg-gray-100 text-brand font-medium">
              {currentUser.first_name.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
        </Button>
      </div>

      {/* Help Dialog */}
      <HelpDialog open={showHelpDialog} onOpenChange={setShowHelpDialog} />
    </header>
  );
}

export default Header;
